import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { SpecDesignLink, SpecDesignLinks } from '../../../lib/api/spec-requirements.js';

export interface DesignLinksEditorProps {
  /** 今登録されているデザイン成果物。無い案件では null */
  designLinks: SpecDesignLinks | null;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因。件数・URL の形・書き込み権限はここに API のメッセージが入る */
  error: unknown;
  /** 保存が通ったときに残った注意（上限に近づいた） */
  warnings: string[];
  /** 保存する */
  onSave: (links: SpecDesignLink[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/** 空の行。名前と URL の 2 つだけ。 @graph-connects none */
const EMPTY_ROW: SpecDesignLink = { name: '', url: '' };

/**
 * デザイン成果物を編集する。名前と URL の 2 列の表。
 *
 * **判定は API が持つ。** 件数の上限・URL のスキーム（`http://` / `https://`）・名前の長さは
 * `check_design_links` と同じ規則で API 側にある。画面は**今の件数だけ**を出し、超過や不正な URL は
 * 保存時に返るメッセージをそのまま見せる。上限やスキームの一覧を画面に持たせると、変わったときに
 * 黙ってずれる。
 *
 * **ラフ / 確定の列は置かない。** 区別が要るなら名前に書く（人が決めたこと）。列にすると、
 * どちらでもない成果物を入れるときに必ずどちらかを選ばされる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件のデザイン成果物（名前と URL の組）を行の追加・削除つきの表で編集して保存する
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function DesignLinksEditor({
  designLinks,
  saving,
  error,
  warnings,
  onSave,
  onCancel,
}: DesignLinksEditorProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SpecDesignLink[]>(
    designLinks === null || designLinks.links.length === 0 ? [EMPTY_ROW] : designLinks.links,
  );

  /** 1 行の 1 列だけを差し替える。 */
  const edit = (index: number, field: keyof SpecDesignLink, value: string) => {
    setRows((current) => current.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  /** 保存できなかった理由を人が読む形にする。 */
  const reason = (cause: unknown): string => {
    if (cause instanceof ApiError) {
      if (cause.status === 403) return t('spec.designLinks.forbidden');
      return cause.message;
    }
    if (cause instanceof Error) return cause.message;
    return t('spec.designLinks.saveFailed');
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(rows);
      }}
    >
      <p className="text-xs text-[var(--color-text-muted)]">{t('spec.designLinks.hint')}</p>
      <p className="text-xs text-[var(--color-text-muted)]">{t('spec.designLinks.notReviewed')}</p>

      <ul className="mt-2 space-y-2">
        {rows.map((row, index) => (
          <li key={index} className="flex gap-2 items-start">
            <input
              type="text"
              className="w-56 border border-[var(--color-border)] rounded px-2 py-1 text-sm"
              value={row.name}
              aria-label={t('spec.designLinks.nameLabel', { row: index + 1 })}
              onChange={(e) => {
                edit(index, 'name', e.target.value);
              }}
            />
            <input
              type="text"
              className="flex-1 border border-[var(--color-border)] rounded px-2 py-1 text-sm font-mono"
              value={row.url}
              placeholder="https://"
              aria-label={t('spec.designLinks.urlLabel', { row: index + 1 })}
              onChange={(e) => {
                edit(index, 'url', e.target.value);
              }}
            />
            <button
              type="button"
              className="text-xs px-2 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
              aria-label={t('spec.designLinks.removeAria', { row: index + 1 })}
              onClick={() => {
                setRows((current) => current.filter((_, i) => i !== index));
              }}
            >
              {t('spec.designLinks.remove')}
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-2 flex items-baseline gap-3">
        <button
          type="button"
          className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={() => {
            setRows((current) => [...current, EMPTY_ROW]);
          }}
        >
          {t('spec.designLinks.addRow')}
        </button>
        <span className="text-xs text-[var(--color-text-muted)]">
          {t('spec.designLinks.count', { total: rows.length })}
        </span>
      </div>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(error)}
        </p>
      )}

      {warnings.length === 0 ? null : (
        <div className="mt-3 text-sm text-[var(--color-text-primary)]">
          <p className="font-semibold text-[var(--color-warning-text)]">
            {t('spec.designLinks.warnings')}
          </p>
          <ul className="list-disc pl-5">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {saving ? t('spec.designLinks.saving') : t('spec.designLinks.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.designLinks.cancel')}
        </button>
      </div>
    </form>
  );
}
